<template>
  <!-- Zapier 2.0 is not offered to this company: the subscriptions page as it was. -->
  <ZapierSubscriptions v-if="loaded && !hubOffered" />

  <Page v-else>
    <div v-if="!loaded" class="flex justify-center py-10">
      <Spinner />
    </div>

    <div v-else class="max-w-5xl" data-cy="zapier-hub">
      <h1 class="page-header-1 mb-4">Zapier</h1>

      <nav class="mb-4 border-b border-gray-200" aria-label="Zapier sections">
        <ul class="-mb-px flex flex-wrap text-sm font-medium text-gray-500">
          <li v-for="tab in TABS" :key="tab.key" class="me-2">
            <a
              :href="tabHref(tab.key)"
              class="inline-block rounded-t-lg border-b-2 p-4"
              :class="
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent hover:border-gray-300 hover:text-gray-600'
              "
              :aria-current="activeTab === tab.key ? 'page' : undefined"
              :data-cy="`zapier-hub-tab:${tab.key}`"
              @click.prevent="selectTab(tab.key)"
              >{{ tab.label }}</a
            >
          </li>
        </ul>
      </nav>

      <ZapierKeys
        v-if="activeTab === 'keys'"
        :zapier-integrations="zapierIntegrations"
      />
      <ZapierSubscriptions v-else embedded />
    </div>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import Page from "@/components/Page.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import type { ZapierIntegrations } from "./zapierKey";
import ZapierKeys from "./ZapierKeys.vue";
import ZapierSubscriptions from "./ZapierSubscriptions.vue";

// The Zapier hub: the company's API keys (1.x and 2.0, as they apply) and its
// subscriptions. The tab lives in the query string, as on the API access page.
// With Zapier 2.0 not offered (zapier_engine off) there is nothing to switch
// between, so the page is the subscriptions page it was before the hub.
const TABS = [
  { key: "keys", label: "API keys" },
  { key: "subscriptions", label: "Subscriptions" }
] as const;
type TabKey = (typeof TABS)[number]["key"];

const HUB_PATH = "/marketplace/zapier";

const store = useStore();
const route = useRoute();
const router = useRouter();

const loaded = ref(false);
const zapierIntegrations = computed<ZapierIntegrations>(
  () => store.state.zapierIntegrations
);
const hubOffered = computed(
  () => !!zapierIntegrations.value?.zapier_v2?.available
);

const activeTab = computed<TabKey>(() =>
  route.query.tab === "subscriptions" ? "subscriptions" : "keys"
);

function tabHref(key: TabKey): string {
  return key === "subscriptions" ? `${HUB_PATH}?tab=subscriptions` : HUB_PATH;
}

function selectTab(key: TabKey) {
  if (key === activeTab.value) return;
  router.replace({ query: key === "subscriptions" ? { tab: key } : {} });
}

// Marketplace › Zapier, set after the first load so it lands after the router's
// deferred regeneration (the embedded subscriptions page leaves it to this).
function setTrail() {
  store.dispatch("setBreadcrumbs", [
    { label: "Marketplace", url: "/marketplace" },
    { label: "Zapier" }
  ]);
}

onMounted(async () => {
  try {
    await store.dispatch("fetchZapierIntegrations");
  } finally {
    loaded.value = true;
    setTrail();
  }
});
</script>
